import type { SettingsPage } from "../types";
import { FONT_FAMILIES } from "../../editor/FontFamilyPicker";
import { FONT_SIZES } from "../../editor/FontSizePicker";
import { precacheForOffline } from "../../offline/precacheForOffline";
import { clearOfflineCache } from "../../offline/clearOfflineCache";
import { DEFAULT_LONG_PRESS_ACTION, LONG_PRESS_ACTION_OPTIONS } from "../../editor/longPressAction";

const fontFamilyOptions = FONT_FAMILIES.map((f, i) =>
  i === 0 ? { value: f.value, label: "System default" } : { value: f.value, label: f.label }
);
const fontSizeOptions = [
  { value: "", label: "System default" },
  ...FONT_SIZES.map((size) => ({ value: `${size}pt`, label: `${size}pt` })),
];

const generalSettings: SettingsPage = {
  id: "general",
  label: "General",
  order: 0,
  items: [
    {
      key: "editingHeading",
      label: "Editing",
      type: "heading",
      default: "",
    },
    {
      key: "defaultEditMode",
      label: "Open documents in edit mode",
      type: "toggle",
      default: true,
      description: "Applies the next time the app starts or a document is opened.",
    },
    {
      key: "autosaveDelayMs",
      label: "Autosave delay (ms)",
      type: "number",
      default: 500,
      min: 100,
      max: 5000,
      step: 100,
      description: "How long to wait after an edit before saving automatically.",
    },
    {
      key: "defaultFontFamily",
      label: "Default font family",
      type: "select",
      default: "",
      options: fontFamilyOptions,
      description:
        "Applied to brand-new documents only — existing documents are unaffected. " +
        "Not yet included when exporting to Word.",
    },
    {
      key: "defaultFontSize",
      label: "Default font size",
      type: "select",
      default: "",
      options: fontSizeOptions,
      description: "Applied to brand-new documents only — existing documents are unaffected.",
    },
    {
      key: "indentStepPx",
      label: "Indent step (px)",
      type: "number",
      default: 24,
      min: 8,
      max: 96,
      step: 2,
      description:
        "How far Format > Increase/Decrease Indent shifts a paragraph per level. " +
        "Also changes the indent width used when exporting to Word.",
    },
    {
      key: "layoutHeading",
      label: "Layout",
      type: "heading",
      default: "",
    },
    {
      key: "hidePlugin",
      label: "Hide plugin panel",
      type: "toggle",
      default: false,
      description: "Hides the yaqatw plugin panel on the right. Also toggleable from View > Hide plugin.",
    },
    {
      key: "showParagraphNumbers",
      label: "Show paragraph numbers",
      type: "toggle",
      default: false,
      description:
        "Displays each paragraph's stable number in the left margin. Also toggleable from View > Paragraph Numbers.",
    },
    {
      key: "headerPinned",
      label: "Pin header",
      type: "toggle",
      default: true,
      description:
        "Keeps the logo/project-name header visible above the menu bar. Also toggleable from the pin button in the header itself.",
    },
    {
      key: "showWordCountStatusBar",
      label: "Show word count in status bar",
      type: "toggle",
      default: true,
      description: "Displays a live word/character count at the bottom of the editor.",
    },
    {
      key: "touchHeading",
      label: "Touch",
      type: "heading",
      default: "",
      description: "Only affects touch devices — a mouse right-click always opens the actions menu.",
    },
    {
      key: "longPressAction",
      label: "Long-press on text",
      type: "select",
      default: DEFAULT_LONG_PRESS_ACTION,
      options: LONG_PRESS_ACTION_OPTIONS,
      description:
        "Suppressing the browser's own long-press also cancels its word selection and drag handles, " +
        "so selecting text and opening the actions menu can't share one gesture. Both of the first " +
        "two options keep the selection gesture intact.",
    },
    {
      key: "offlineHeading",
      label: "Offline",
      type: "heading",
      default: "",
    },
    {
      key: "prepareOffline",
      label: "Prepare for offline use",
      type: "action",
      default: "",
      description:
        "Caches the app's code so it keeps working without a network connection. Re-run this after updating if you plan to go offline.",
      onClick: async () => {
        const { cached, failed } = await precacheForOffline();
        if (cached === 0 && failed === 0) {
          throw new Error("Nothing to cache yet — reload the page once, then try again.");
        }
        if (failed > 0) {
          throw new Error(`Cached ${cached} file(s) — ${failed} failed (offline use may be incomplete).`);
        }
      },
    },
    {
      key: "clearOfflineCache",
      label: "Clear offline cache",
      type: "action",
      default: "",
      description:
        "Deletes all cached files and unregisters the offline service worker. Use this if the app seems stuck on an old version, then reload the page.",
      onClick: () => clearOfflineCache(),
    },
  ],
};

export default generalSettings;
